import { EInvoicingAddress, EntityType, getDefaultAddressSchemeTypeByCountry, ProcessingOverlay } from '@components'

import {
  BorderRadius,
  Button,
  ButtonsGroup,
  Color,
  Flex,
  FormItemsGroup,
  Icon,
  Input,
  Link,
  Modal,
  ModalProps,
  Spacing,
  Text,
  Tooltip,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { getEInvoicingInfoLink } from '@features/invoices/utils/getEInvoicingInfoLink'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { ModalId } from '../../../../enums/modalId'
import { useForm } from '../../../../hooks'
import { DEMO_INVOICE_E_INVOICING_ADDRESS } from '../../constants/demoInvoiceEInvoicingAddress'
import { useDemoInvoiceFakeSend } from '../../hooks/useDemoInvoiceFakeSend'
import { useTrackDemoInvoiceEditor } from '../../hooks/useTrackDemoInvoiceEditor'
import { DemoInvoiceSendAsEInvoiceForm } from '../../types/demoInvoiceSendAsEInvoiceForm'
import { getDemoInvoiceSendAsEInvoiceFormSchema } from '../../utils/getDemoInvoiceSendAsEInvoiceFormSchema'

interface DemoInvoiceSendAsEInvoiceProps extends ModalProps {
  companyName: string
  countryId?: string
  onSent: () => void
}

const DemoInvoiceSendAsEInvoice = ({
  companyName,
  countryId,
  onSent,
  ...modalProps
}: DemoInvoiceSendAsEInvoiceProps): ReactElement => {
  const { t } = useTranslation()
  const { user } = useCurrentUser()
  const { trackDemoInvoiceEditor } = useTrackDemoInvoiceEditor()
  const { close } = useModal(ModalId.DemoInvoiceSendAsEInvoiceModal)
  const { fakeSend, isSending } = useDemoInvoiceFakeSend({
    modalId: ModalId.DemoInvoiceSendAsEInvoiceModal,
    onSuccess: onSent,
  })

  const validationSchema = useMemo(() => getDemoInvoiceSendAsEInvoiceFormSchema(t), [t])
  const defaultValues = useMemo(
    (): DemoInvoiceSendAsEInvoiceForm => ({
      addressingLine: DEMO_INVOICE_E_INVOICING_ADDRESS,
      companyName,
      entityType: EntityType.Private,
    }),
    [companyName],
  )

  const {
    Form,
    FormItem,
    formState: { errors },
    handleSubmit,
    setValue,
    trigger,
    watch,
  } = useForm({ defaultValues, validationSchema })

  const [addressingLine, entityType] = watch(['addressingLine', 'entityType'])
  const addressingLineErrorMessage = errors?.addressingLine?.message
  const isPublicEntity = entityType === EntityType.Public
  const schemeType = getDefaultAddressSchemeTypeByCountry(isPublicEntity, countryId) ?? ''

  const handleAddressingLineChange = useCallback(
    (newAddressingLine?: string) => {
      setValue('addressingLine', newAddressingLine ?? '')

      if (addressingLineErrorMessage) {
        trigger('addressingLine')
      }
    },
    [addressingLineErrorMessage, setValue, trigger],
  )

  const handleEntityTypeChange = useCallback(
    (newEntityType?: EntityType) => {
      setValue('entityType', newEntityType)
    },
    [setValue],
  )

  const handleSchemeTypeChange = useCallback(() => undefined, [])

  const handleSendButtonClick = useCallback(() => {
    trackDemoInvoiceEditor('Send as e-invoice modal - Send now clicked')
    handleSubmit(fakeSend)()
  }, [fakeSend, handleSubmit, trackDemoInvoiceEditor])

  return (
    <Modal {...modalProps} closable={!isSending} size="s">
      <Modal.Header title={t('external_invoices.send_as_e_invoice.modal.e_invoicing_address.header')} />
      <Modal.Body>
        {isSending && <ProcessingOverlay borderRadius={BorderRadius.R12} color={Color.White} withAnimation />}
        <Form>
          <FormItemsGroup rowSpacing={Spacing.S12}>
            <Text>{t('external_invoices.send_as_e_invoice.modal.subtitle.provide_identifier')}</Text>
            <FormItem
              label={t('external_invoices.send_as_e_invoice.modal.field.customer_company_name.label')}
              name="companyName"
              render={({ field: { onChange, ...rest } }) => <Input {...rest} disabled size="sm" />}
            />
            <EInvoicingAddress
              address={isPublicEntity ? '' : addressingLine}
              countryId={countryId}
              eInvoiceAddressInputErrorMessage={addressingLineErrorMessage}
              entityType={entityType}
              inputLabel={
                <Flex flexDirection="row" gap={Spacing.S4}>
                  <Text colorVariant="secondary">
                    {t('external_invoices.send_as_e_invoice.modal.field.addressing_line.label')}
                  </Text>
                  <Tooltip
                    label={t('external_invoices.send_as_e_invoice.modal.field.addressing_line.label_tooltip')}
                    placement="top"
                  >
                    <Icon icon="circleQuestionMark" size="xs" />
                  </Tooltip>
                </Flex>
              }
              inputPlaceholder={
                isPublicEntity
                  ? undefined
                  : t('external_invoices.send_as_e_invoice.modal.field.addressing_line.placeholder')
              }
              onEInvoiceAddressEntityTypeChange={handleEntityTypeChange}
              onEInvoiceAddressInputChange={handleAddressingLineChange}
              onEInvoiceAddressSchemeTypeChange={handleSchemeTypeChange}
              schemeType={schemeType}
              size="sm"
            />
            {user && (
              <Text colorVariant="secondary">
                <Trans
                  components={{
                    a: (
                      <Link
                        href={getEInvoicingInfoLink(user.locale)}
                        rel="noopener noreferrer"
                        target="_blank"
                        withAnimation
                      ></Link>
                    ),
                  }}
                  i18nKey="external_invoices.send_as_e_invoice.modal.e_invoicing_address.learn_more_link"
                />
              </Text>
            )}
          </FormItemsGroup>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Flex alignItems="center" flexGrow={1} justifyContent="flex-end">
          <ButtonsGroup>
            <Button disabled={isSending} onClick={close} variant="text">
              {t('external_invoices.send_as_e_invoice.modal.buttons.cancel')}
            </Button>
            <Button disabled={isSending || isPublicEntity} loading={isSending} onClick={handleSendButtonClick}>
              {t('external_invoices.send_as_e_invoice.modal.buttons.send_now')}
            </Button>
          </ButtonsGroup>
        </Flex>
      </Modal.Footer>
    </Modal>
  )
}

export const DemoInvoiceSendAsEInvoiceModal = withModalConditionalRender(DemoInvoiceSendAsEInvoice)
